import React, { useState, useEffect, useCallback } from 'react';
import client from '../../api/client';
import {
  ShoppingBag, Users, Package, Wallet,
  TrendingUp, RefreshCw,
} from 'lucide-react';

function formatMoney(value) {
  const num = Number(value);
  if (Number.isNaN(num)) return '—';
  return num.toLocaleString('ru-RU', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

const CARDS = [
  {
    key: 'orders_count',
    label: 'Заказы',
    Icon: ShoppingBag,
    color: 'bg-[#4A6FA5]/10 text-[#4A6FA5]',
    accent: 'text-[#4A6FA5]',
    format: (v) => v ?? '—',
  },
  {
    key: 'users_count',
    label: 'Пользователи',
    Icon: Users,
    color: 'bg-[#4A6FA5]/10 text-[#4A6FA5]',
    accent: 'text-[#4A6FA5]',
    format: (v) => v ?? '—',
  },
  {
    key: 'products_count',
    label: 'Товары',
    Icon: Package,
    color: 'bg-[#E8A838]/10 text-[#E8A838]',
    accent: 'text-[#E8A838]',
    format: (v) => v ?? '—',
  },
  {
    key: 'revenue_year',
    label: 'Доход за год',
    Icon: Wallet,
    color: 'bg-[#6B9B74]/10 text-[#6B9B74]',
    accent: 'text-[#6B9B74]',
    format: (v) => (v == null ? '—' : `${formatMoney(v)} ₽`),
  },
];

export default function StatsAdmin() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadStats = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await client.get('/admin/dashboard/stats');
      setStats(res.data);
    } catch (err) {
      console.error('Ошибка загрузки статистики:', err);
      setError('Не удалось загрузить статистику');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadStats(); }, [loadStats]);

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-[#6B9B74]" />
          <h2 className="text-lg font-bold text-[#0f172a]">Дашборд</h2>
        </div>
        <button
          onClick={loadStats}
          disabled={loading}
          className="flex items-center gap-1.5 rounded-lg border border-[#e2e8f0] px-3 py-1.5 text-xs font-medium text-[#64748b] transition hover:bg-[#f8fafc] active:scale-95 disabled:opacity-50"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          Обновить
        </button>
      </div>

      {error && (
        <div className="rounded-xl border border-[#D64545]/30 bg-[#D64545]/10 p-4 text-sm text-[#D64545]">
          {error}
        </div>
      )}

      {loading && !stats ? (
        <div className="grid grid-cols-2 gap-3">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="animate-pulse rounded-xl border border-[#e2e8f0] bg-white p-4 shadow-sm">
              <div className="mb-3 h-4 w-20 rounded bg-[#e2e8f0]" />
              <div className="h-7 w-16 rounded bg-[#e2e8f0]" />
            </div>
          ))}
        </div>
      ) : stats ? (
        <div className="grid grid-cols-2 gap-3">
          {CARDS.map(({ key, label, Icon, color, accent, format }) => (
            <div
              key={key}
              className="rounded-xl border border-[#e2e8f0] bg-white p-4 shadow-sm transition hover:shadow-md"
            >
              <div className={`mb-2 inline-flex items-center justify-center rounded-lg p-2 ${color}`}>
                <Icon className="h-4 w-4" />
              </div>
              <p className="text-xs font-medium text-[#64748b]">{label}</p>
              <p className={`mt-0.5 text-xl font-bold ${accent}`}>
                {format(stats[key])}
              </p>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
